import { MapPinOff } from 'lucide-react';

import { FeriadoForm } from '@/components/feriados/FeriadoForm';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/spinner';
import { useLancarFeriado, useLugaresDoAcervo } from '@/hooks/useMeusFeriados';
import { classifyError } from '@/lib/errors';

import { opcoesDoAcervo } from './lugaresDoAcervo';

interface Props {
  onClose: () => void;
}

/**
 * Lançar fora de um prazo (docs/PLANO_TELAS_TAGS_FERIADOS.md, D3): o mesmo formulário da
 * conta do prazo, com os lugares do acervo da conta. Lugar fora do acervo não aparece —
 * o dia lá não muda conta nenhuma e só chegaria à fila do admin.
 */
export function LancarAvulsoDialog({ onClose }: Props) {
  const lancar = useLancarFeriado();
  const { data, isLoading, error, refetch } = useLugaresDoAcervo(true);
  const lugares = opcoesDoAcervo(data);

  return (
    // Fechar com o envio em curso perderia o erro (o callback não roda desmontado).
    <Dialog open onOpenChange={(open) => !open && !lancar.isPending && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Lançar feriado ou suspensão</DialogTitle>
          <DialogDescription>
            Vale na hora nas contas dos processos daquele lugar: os vencimentos são refeitos e a agenda acompanha. O
            JurisApp confere o ato e pode passar a contar o dia para todos.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner size={32} label="Carregando os lugares do acervo" />
          </div>
        ) : error ? (
          <ErrorState
            title="Não deu para carregar os lugares do acervo"
            description={classifyError(error).message}
            actions={[{ label: 'Tentar de novo', onClick: () => void refetch() }]}
          />
        ) : lugares.length === 0 ? (
          <EmptyState
            icon={<MapPinOff />}
            title="Nenhum lugar no acervo"
            description="Os lugares saem dos processos da conta, pelo número do processo. Sem processo cadastrado, não há onde o dia mudaria um prazo."
          />
        ) : (
          <FeriadoForm
            lugares={lugares}
            // Um lugar só: já escolhido. Mais de um: a pessoa escolhe, para não lançar no errado.
            lugarInicial={lugares.length === 1 ? lugares[0].value : ''}
            buscarLugar
            lancar={lancar}
            onLancado={onClose}
            onCancelar={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
